/**
 * useAIUsage — one item's AI usage, and the calls behind any one run.
 *
 * Two fetches with different lifetimes, which is why they are two hooks. The
 * summary is what the pane opens onto and re-reads on Refresh. A run's calls
 * are read only when someone expands that run, and then kept: expanding,
 * collapsing and re-expanding the same run is a comparison someone makes
 * while reading, and re-fetching on every toggle would make it flicker.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import apis from '../../../utils/apis';
import { isAiUsageEnabled } from '../../../config/featureFlags';
import {
  AI_USAGE_ENDPOINTS,
  AIUsageCall,
  AIUsageEntityType,
  AIUsageSummary,
  normaliseRunDetail,
  normaliseUsage,
  readErrorMessage,
} from './aiUsageModel';

export interface UseAIUsageResult {
  usage: AIUsageSummary | null;
  loading: boolean;
  /**
   * A readable message, or null — ALWAYS a string when set.
   *
   * The pane renders this as a JSX child, so an object here is a crash
   * rather than a bad label. `readErrorMessage` is what guarantees it.
   */
  error: string | null;
  refresh: () => Promise<void>;
}

export const useAIUsage = (
  entityType: AIUsageEntityType,
  entityId: string | undefined
): UseAIUsageResult => {
  const [usage, setUsage] = useState<AIUsageSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /*
    The pane is remounted by a dock move and re-rendered by every parent
    update, so a resolved fetch has to know whether it is still the current
    one before it writes state.
  */
  const liveRequest = useRef(0);

  const load = useCallback(async () => {
    /*
      Hidden regions make NO request. The panes are already gone there (the
      surfaces drop the pane id), so this is the belt to that braces: a
      surface that ever forgets still cannot put usage traffic on the wire.
      See `config/featureFlags.isAiUsageEnabled`.
    */
    if (!isAiUsageEnabled()) {
      setUsage(null);
      return;
    }
    if (!entityId) {
      setUsage(null);
      return;
    }
    const ticket = ++liveRequest.current;
    setLoading(true);
    setError(null);
    try {
      const response = await apis.get(AI_USAGE_ENDPOINTS.summary, {
        params: { entity_type: entityType, entity_id: entityId },
      });
      if (ticket !== liveRequest.current) return;
      setUsage(normaliseUsage(response?.data));
    } catch (err: any) {
      if (ticket !== liveRequest.current) return;
      /*
        A 404 is NOT an error here. Until the read API ships, and for any
        entity the usage service has never recorded a run against, "no usage"
        is the honest answer — and the empty state says so far better than a
        red banner about a missing route.
      */
      if (err?.response?.status === 404) {
        setUsage(normaliseUsage({ entity_type: entityType, entity_id: entityId, runs: [] }));
      } else {
        setUsage(null);
        setError(readErrorMessage(err, 'Failed to load AI usage'));
      }
    } finally {
      if (ticket === liveRequest.current) setLoading(false);
    }
  }, [entityType, entityId]);

  useEffect(() => { load(); }, [load]);

  return { usage, loading, error, refresh: load };
};

export interface UseAIUsageRunCallsResult {
  /** Calls per run id, for the runs that have been expanded at least once. */
  callsByRun: Record<string, AIUsageCall[]>;
  loadingRunId: string | null;
  /** No-op for a run whose calls are already held. */
  loadCalls: (runId: string) => void;
}

export const useAIUsageRunCalls = (): UseAIUsageRunCallsResult => {
  const [callsByRun, setCallsByRun] = useState<Record<string, AIUsageCall[]>>({});
  const [loadingRunId, setLoadingRunId] = useState<string | null>(null);
  /* Reads synchronously, so two expands in one tick cannot both fire. */
  const requested = useRef<Set<string>>(new Set());

  const loadCalls = useCallback((runId: string) => {
    if (!isAiUsageEnabled()) return;
    if (!runId || requested.current.has(runId)) return;
    requested.current.add(runId);
    setLoadingRunId(runId);
    apis.get(AI_USAGE_ENDPOINTS.run(runId))
      .then((response) => {
        const { calls } = normaliseRunDetail(response?.data);
        setCallsByRun((prev) => ({ ...prev, [runId]: calls }));
      })
      .catch(() => {
        /*
          An empty list, not a thrown pane. The run's own totals are already
          on screen from the summary — the drill-down failing costs the reader
          the breakdown, not the figure they came for.
        */
        setCallsByRun((prev) => ({ ...prev, [runId]: [] }));
        requested.current.delete(runId);
      })
      .finally(() => {
        setLoadingRunId((current) => (current === runId ? null : current));
      });
  }, []);

  return { callsByRun, loadingRunId, loadCalls };
};

export default useAIUsage;
